import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiHome,
  FiShoppingBag,
  FiBarChart2,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiTrendingUp,
  FiUsers,
  FiCreditCard,
  FiDollarSign,
  FiGlobe,
} from 'react-icons/fi';
import { removeToken, isAuthenticated } from '../lib/auth';
import api from '../lib/api';
import styles from '../pages/admin/Dashboard.module.css';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }

    // Fetch current admin user
    api.get('/auth/me')
      .then((res) => {
        setAdminUser(res.data.user);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch admin user:', err);
        removeToken();
        navigate('/admin/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    removeToken();
    navigate('/admin/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  // Define navigation items with role-based visibility
  const getNavItems = () => {
    const role = adminUser?.role || '';

    const allItems = [
      { 
        to: '/admin/dashboard', 
        icon: <FiHome size={18} />, 
        label: t('admin.dashboard'),
        roles: ['SUPER_ADMIN', 'ADMIN', 'AFFILIATE_MANAGER', 'MANAGER', 'OPS']
      },
      { 
        to: '/admin/orders', 
        icon: <FiShoppingBag size={18} />, 
        label: t('admin.orders'),
        roles: ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPS']
      },
      { 
        to: '/admin/stock', 
        icon: <FiBarChart2 size={18} />, 
        label: t('admin.stock'),
        roles: ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPS']
      },
      { 
        to: '/admin/capacity', 
        icon: <FiBarChart2 size={18} />, 
        label: t('admin.capacity'),
        roles: ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPS']
      },
      { 
        to: '/admin/affiliates', 
        icon: <FiTrendingUp size={18} />, 
        label: 'Affiliates',
        roles: ['SUPER_ADMIN', 'ADMIN', 'AFFILIATE_MANAGER']
      },
      { 
        to: '/admin/promo-codes', 
        icon: <FiCreditCard size={18} />, 
        label: 'Promo Codes',
        roles: ['SUPER_ADMIN', 'ADMIN', 'AFFILIATE_MANAGER']
      },
      { 
        to: '/admin/withdraws', 
        icon: <FiDollarSign size={18} />, 
        label: 'Withdrawals',
        roles: ['SUPER_ADMIN', 'ADMIN', 'AFFILIATE_MANAGER']
      },
      { 
        to: '/admin/partners', 
        icon: <FiUsers size={18} />, 
        label: 'Partenaires',
        roles: ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPS']
      },
      { 
        to: '/admin/team', 
        icon: <FiUsers size={18} />, 
        label: t('admin.teamManagement'),
        roles: ['SUPER_ADMIN']
      },
      { 
        to: '/admin/settings', 
        icon: <FiSettings size={18} />, 
        label: t('admin.settings'),
        roles: ['SUPER_ADMIN', 'ADMIN', 'AFFILIATE_MANAGER', 'MANAGER', 'OPS']
      },
    ];

    return allItems.filter(item => item.roles.includes(role));
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        color: 'var(--text-muted)'
      }}>
        Loading...
      </div>
    );
  }

  const navItems = getNavItems();

  return (
    <div className={styles.dashboard}>
      {/* Sidebar overlay for mobile */}
      <div
        className={`${styles.sidebarOverlay} ${sidebarOpen ? styles.open : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.open : ''}`}>
        <div className={styles.sidebarLogo}>
          OFMIG<span>.SHOP</span>
        </div>
        
        <nav className={styles.sidebarNav}>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`${styles.sidebarLink} ${
                location.pathname === item.to ? styles.active : ''
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          {/* Language selector */}
          <button 
            className={styles.logoutBtn}
            onClick={toggleLanguage}
            style={{ marginBottom: '8px' }}
          >
            <FiGlobe size={18} />
            {i18n.language === 'fr' ? 'English' : 'Français'}
          </button>

          {/* Logout button */}
          <button className={styles.logoutBtn} onClick={handleLogout}>
            <FiLogOut size={18} />
            {t('admin.logout')}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <main className={styles.main}>
        <div className={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className={styles.mobileToggle}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <FiMenu size={18} />
            </button>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
